<template>
	<div id="app" :class="extraClasses">
		<div class="app-layout" @click="closeSidebars" v-if="$store.state.app.appLayout">
			<css-variables :key="$store.state.auth.userChange"/>
			<main-nav v-if="!$store.state.popups.showUserActivationPopup"/>
			<mobile-nav v-if="!$store.state.popups.showUserActivationPopup"/>
			<user-activation-popup v-if="$store.state.popups.showUserActivationPopup"/>
			<user-deletion-popup v-if="$store.state.popups.showUserDeletionPopup"/>
			<complete-registration-popup v-if="$store.state.popups.showCompleteRegistrationPopup"/>
			<div class="app-content" :style="`padding-top: ${safeAreaTopHeight}px;`"
			     :class="$store.state.app.appLoading ? 'loading' : ''" v-else>
				<app-header v-if="!$store.state.app.native"/>
				<native-header v-else/>
				<router-view/>
				<app-footer/>
			</div>
			<notifications-sidebar v-if="$store.state.auth.user.id"/>
			<settings-sidebar/>
			<documents-create-form-sidebar/>
			<documents-edit-form-sidebar/>
			<contracts-edit-form-sidebar/>
			<personal-documents-create-form-sidebar/>
			<personal-documents-edit-form-sidebar/>
			<cards-create-form-sidebar/>
			<protocol-detail-sidebar/>
			<online-services-sidebar/>
			<portfolio-sidebar/>
			<property-create-form-sidebar/>
			<property-edit-form-sidebar/>
			<receipts-create-form-sidebar/>
			<receipts-edit-form-sidebar/>
			<barcode-overlay v-if="$store.state.overlays.showBarcodeOverlay"/>
			<safe-area-top :height="safeAreaTopHeight"/>
			<safe-area-bottom/>
			<setup-pin-popup v-if="$store.state.popups.showSetupPinPopup"/>
			<ocr-popup v-if="$store.state.popups.showOcrPopup" :link="$store.state.popups.showOcrPopup"/>
		</div>
		<div class="public-layout" :class="pageClass" v-else>
			<public-header v-if="!$store.state.app.native"/>
			<css-variables :key="$store.state.auth.userChange"/>
			<router-view/>
			<login-popup/>
			<registration-popup/>
			<reset-password-popup/>
			<public-footer v-if="!$store.state.app.native"/>
			<div class="loading-overlay" v-if="$store.state.app.appLoading"></div>
		</div>
		<offline-cards-list-sidebar/>
		<cards-edit-form-sidebar/>
	</div>
</template>

<style lang="scss">
@import "@/assets/sass/screen";
</style>

<script>
import CssVariables from "./components/globals/CssVariables";
import MainNav from "./components/partials/MainNav";
import MobileNav from "./components/partials/MobileNav";
import AppHeader from "./components/partials/AppHeader";
import NativeHeader from "./components/partials/NativeHeader";
import NotificationsSidebar from "./components/sidebars/NotificationsSidebar";
import DocumentsCreateFormSidebar from "./components/sidebars/DocumentsCreateFormSidebar";
import DocumentsEditFormSidebar from "./components/sidebars/DocumentsEditFormSidebar";
import ContractsEditFormSidebar from "./components/sidebars/ContractsEditFormSidebar";
import SettingsSidebar from "./components/sidebars/SettingsSidebar";
import PersonalDocumentsCreateFormSidebar from "./components/sidebars/PersonalDocumentsCreateFormSidebar";
import PersonalDocumentsEditFormSidebar from "./components/sidebars/PersonalDocumentsEditFormSidebar";
import CardsCreateFormSidebar from "./components/sidebars/CardsCreateFormSidebar";
import CardsEditFormSidebar from "./components/sidebars/CardsEditFormSidebar";
import ProtocolDetailSidebar from "./components/sidebars/ProtocolDetailSidebar";
import OnlineServicesSidebar from "@/components/sidebars/OnlineServicesSidebar";
import PortfolioSidebar from "@/components/sidebars/PortfolioSidebar";
import PublicHeader from "./components/partials/PublicHeader";
import PublicFooter from "@/components/partials/PublicFooter";
import LoginPopup from "./components/popups/LoginPopup";
import RegistrationPopup from "./components/popups/RegistrationPopup";
import ResetPasswordPopup from "./components/popups/ResetPasswordPopup";
import BarcodeOverlay from "./components/overlays/BarcodeOverlay";
import SafeAreaTop from "./components/partials/SafeAreaTop";
import SafeAreaBottom from "./components/partials/SafeAreaBottom";
import SetupPinPopup from "./components/popups/SetupPinPopup";
import UserActivationPopup from "./components/popups/UserActivationPopup";
import automaticLogoutMixin from "@/mixins/automaticLogoutMixin";
import {SocialLogin} from '@capgo/capacitor-social-login';
import AppFooter from "./components/partials/AppFooter";
import OcrPopup from "./components/popups/OcrPopup";
import CompleteRegistrationPopup from "./components/popups/CompleteRegistrationPopup";
import UserDeletionPopup from "./components/popups/UserDeletionPopup";
import PropertyCreateFormSidebar from "@/components/sidebars/PropertyCreateFormSidebar.vue";
import PropertyEditFormSidebar from "@/components/sidebars/PropertyEditFormSidebar.vue";
import ReceiptsCreateFormSidebar from "@/components/sidebars/ReceiptsCreateFormSidebar.vue";
import ReceiptsEditFormSidebar from "@/components/sidebars/ReceiptsEditFormSidebar.vue";
import OfflineCardsListSidebar from "@/components/sidebars/OfflineCardsListSidebar.vue";
import {PushNotifications} from "@capacitor/push-notifications";

export default {
	name: "App",
	components: {
		CssVariables,
		AppHeader,
		AppFooter,
		NativeHeader,
		MainNav,
		MobileNav,
		NotificationsSidebar,
		SettingsSidebar,
		DocumentsCreateFormSidebar,
		DocumentsEditFormSidebar,
		ContractsEditFormSidebar,
		PersonalDocumentsCreateFormSidebar,
		PersonalDocumentsEditFormSidebar,
		CardsCreateFormSidebar,
		CardsEditFormSidebar,
		ProtocolDetailSidebar,
		OnlineServicesSidebar,
		PortfolioSidebar,
		PublicHeader,
		PublicFooter,
		LoginPopup,
		RegistrationPopup,
		ResetPasswordPopup,
		BarcodeOverlay,
		SafeAreaTop,
		SafeAreaBottom,
		SetupPinPopup,
		UserActivationPopup,
		OcrPopup,
		CompleteRegistrationPopup,
		UserDeletionPopup,
		PropertyCreateFormSidebar,
		PropertyEditFormSidebar,
		ReceiptsCreateFormSidebar,
		ReceiptsEditFormSidebar,
		OfflineCardsListSidebar,
	},
	mixins: [automaticLogoutMixin],
	methods: {
		closeSidebars(e) {
			let target = e.target;
			if (target.classList.contains('default-sidebar')) {
				return false;
			}

			let sidebar = target.closest('.default-sidebar');
			if (sidebar) {
				return false;
			}

			this.$store.dispatch('closeSidebars');
		},
		async initSocialAuth() {
			if (this.$store.state.app.native) {
				let mode, appleConfig;
				if(this.$store.state.app.platform === 'ios'){
					mode = 'online';
					appleConfig = {};
				} else {
					mode = 'offline';
					appleConfig = false;
				}
				mode = 'online';

				await SocialLogin.initialize({
					apple: appleConfig,
					google: {
						webClientId: process.env.VUE_APP_GOOGLE_WEB_ID,
						iOSClientId: process.env.VUE_APP_GOOGLE_IOS_ID,
						mode,
					},
					facebook: {
						appId: process.env.VUE_APP_FB_ID,
						clientToken: process.env.VUE_APP_FB_CLIENT_TOKEN
					}
				});
			} else {
				(function (d, s, id) {
					let js, fjs = d.getElementsByTagName(s)[0];
					if (d.getElementById(id)) {
						return;
					}
					js = d.createElement(s);
					js.id = id;
					js.src = 'https://accounts.google.com/gsi/client';
					fjs.parentNode.insertBefore(js, fjs);
				}(document, 'script', 'google-jssdk'));

				await SocialLogin.initialize({
					facebook: {
						appId: process.env.VUE_APP_FB_ID,
						clientToken: process.env.VUE_APP_FB_CLIENT_TOKEN
					}
				});
			}
		},

		checkInvitation() {
			let urlParams = new URLSearchParams(window.location.search);
			if (this.$store.state.auth.isLoggedIn || (!urlParams.has('invite') && !urlParams.has('agent'))) {
				return false;
			}

			let invite = urlParams.get('invite');
			if (invite) {
				this.axios.post('/api/invite-user', {
					hash: invite,
				}).then(response => {
					this.$store.commit('SET_INVITE_HASH', invite);
					this.$store.commit('SET_INTRANET_USER', response.data.intranet_user);

					if(response.data.already_registered){
						this.$store.dispatch('openLoginPopup');
					} else {
						this.$store.dispatch('openRegistrationPopup');
					}
				});

				return true;
			}

			let agent = urlParams.get('agent');
			if (agent) {
				this.$store.commit('SET_INVITE_AGENT', agent);
				this.$store.dispatch('openRegistrationPopup');
			}
		},
		checkPasswordReset() {
			let urlParams = new URLSearchParams(window.location.search);
			if (this.$store.state.auth.isLoggedIn || !urlParams.has('reset-password')) {
				return false;
			}

			this.$store.dispatch('openResetPasswordPopup');
		},
		checkAfterLoginAction() {
			let urlParams = new URLSearchParams(window.location.search);

			if (this.$store.state.auth.isLoggedIn || !urlParams.has('after-login-action')) {
				return false;
			}

			let platform = urlParams.get('platform');
			let isNative = this.$store.state.app.native;

			if (platform === 'native' && !isNative) {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: this.$t('auth.link_native_only'),
				});
				return false;
			}

			if (platform === 'web' && isNative) {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: this.$t('auth.link_web_only'),
				});
				return false;
			}

			let action = urlParams.get('after-login-action');
			if (action) {
				this.$store.commit('SET_AFTER_LOGIN_ACTION', action);
			}

			let invite = urlParams.get('invite');
			let agent = urlParams.get('agent');
			if (!invite && !agent) {
				this.$store.dispatch('openLoginPopup');
			}
		},
		checkUserMasquerade() {
			let urlParams = new URLSearchParams(window.location.search);
			if (this.$store.state.auth.isLoggedIn || !urlParams.has('user-masquerade')) {
				return false;
			}

			if (this.$store.state.auth.isLoggedIn){
				this.$store.dispatch('processLogout');
			}

			let hash = urlParams.get('user-masquerade');

			this.$store.commit('SET_APP_LOADING');
			this.axios.post('/api/user/masquerade-login', {
				hash: hash,
			}).then((response) => {
				this.$store.dispatch('processLogin', response);
				this.$store.commit('UNSET_APP_LOADING');
			}).catch((error) => {
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: error.response.data.message
				});
				this.$store.commit('UNSET_APP_LOADING');
			});
		},
		initPushNotificationsEvents(){
			if (!this.$store.state.app.native) {
				return;
			}

			PushNotifications.addListener('pushNotificationActionPerformed', (data) => {
				this.$store.commit('SHOW_NOTIFICATIONS');
			});
		}
	},
	computed: {
		safeAreaTopHeight() {
			return this.$store.state.app.safeArea.top;
		},
		safeAreaBottomHeight() {
			return this.$store.state.app.safeArea.bottom;
		},
		extraClasses() {
			let classes = [];

			if (this.$store.state.app.native) {
				classes.push('native');
			} else {
				classes.push('web');
			}

			classes.push(process.env.VUE_APP_TYPE);

			if (!this.$store.state.app.darkMode && this.$store.state.app.appLayout) {
				classes.push('light-mode');
			}

			return classes.join(' ');
		},
		pageClass() {
			let classes = [];
			/*if (this.$store.state.app.appLoading) {
				classes.push('loading');
			}*/

			if (!this.$route.name) {
				classes.join(' ');
				return classes;
			}

			let name = this.$route.name.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
			if (name[0] === '-') {
				name = name.substring(1);
			}

			classes.push(name);

			return classes.join(' ');
		},
	},

	mounted() {
		this.checkInvitation();
		this.checkPasswordReset();
		this.checkUserMasquerade();
		this.checkAfterLoginAction();
		document.body.classList.remove('before-mounted');
	},
	async created() {
		this.$store.dispatch('setSafeArea');
		await this.$store.dispatch('checkDefaultUserLogin');
		if (this.$store.state.app.native && this.$store.state.auth.userDefaultId) {
			this.$store.dispatch('loadOfflineCards');
		}
		this.$store.dispatch('checkAppVersion');
		this.initSocialAuth();
		this.initPushNotificationsEvents();
	}
}
</script>